/**
 * Composant réutilisable pour afficher une card d'item de liste
 */

import { html, unsafeCSS, CSSResultGroup } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ObjetTouristique, FacetId, FacetValue } from "../types/apidae.d";
import {
  WidgetLanguage,
  WidgetTypography,
  WidgetFieldsInfos,
} from "../types/widgets.d";
import { StyleService } from "../services/StyleService";
import { ApidaeVisualHost } from "./ApidaeVisualHost";
import { TranslationService } from "../services/TranslationService";
import "../components/Icon";
import listWidgetStyles from "../styles/list-widget.css?inline";

export interface ListItemCardFieldData {
  value: string;
  url?: string;
}

export interface ListItemCardProps {
  item: ObjetTouristique;
  imageUrl: string | null;
  nom: string;
  displayedFields: WidgetFieldsInfos[];
  typography?: WidgetTypography;
  language: WidgetLanguage;
  apiFilters: Map<FacetId, FacetValue[]>;
  onItemClick: (item: ObjetTouristique) => void;
  onImageError: (itemId: number) => void;
  onImageLoad?: (itemId: number) => void;
  failedImages: Set<number>;
  loadedImages: Set<number>;
  t: (key: string) => string;
  getFieldData: (
    item: ObjetTouristique,
    fieldId: string,
    apiFilters: Map<FacetId, FacetValue[]>,
    language: WidgetLanguage,
  ) => ListItemCardFieldData | null;
  getFieldIconName: (fieldId: string) => string;
  /** Afficher le pictogramme OpenSystem (réservation en ligne) sur la photo */
  hasOpenSystem?: boolean;
  /** Afficher le pictogramme Elloha (réservation en ligne) sur la photo */
  hasElloha?: boolean;
  /** Prix minimum Elloha pour le tooltip "À partir de X €" (optionnel) */
  ellohaPriceFrom?: number | null;
}

@customElement("apidae-list-item-card")
export class ListItemCard extends ApidaeVisualHost {
  static styles: CSSResultGroup = [unsafeCSS(listWidgetStyles)];

  @property({ type: Object })
  item!: ObjetTouristique;

  @property({ type: String, attribute: false })
  imageUrl: string | null = null;

  @property({ type: String, attribute: false })
  nom: string = "";

  @property({ type: Array, attribute: false })
  displayedFields: WidgetFieldsInfos[] = [];

  @property({ type: Object, attribute: false })
  typography?: WidgetTypography;

  @property({ type: String, attribute: false })
  language: WidgetLanguage = WidgetLanguage.FR;

  @property({ type: Object, attribute: false })
  apiFilters: Map<FacetId, FacetValue[]> = new Map();

  @property({ type: Function, attribute: false })
  onItemClick!: (item: ObjetTouristique) => void;

  @property({ type: Function, attribute: false })
  onImageError!: (itemId: number) => void;

  @property({ type: Function, attribute: false })
  onImageLoad?: (itemId: number) => void;

  @property({ type: Object, attribute: false })
  failedImages: Set<number> = new Set();

  @property({ type: Object, attribute: false })
  loadedImages: Set<number> = new Set();

  @property({ type: Function, attribute: false })
  t!: (key: string) => string;

  @property({ type: Function, attribute: false })
  getFieldData!: (
    item: ObjetTouristique,
    fieldId: string,
    apiFilters: Map<FacetId, FacetValue[]>,
    language: WidgetLanguage,
  ) => ListItemCardFieldData | null;

  @property({ type: Function, attribute: false })
  getFieldIconName!: (fieldId: string) => string;

  @property({ type: Boolean, attribute: false })
  hasOpenSystem: boolean = false;

  @property({ type: Boolean, attribute: false })
  hasElloha: boolean = false;

  @property({ type: Number, attribute: false })
  ellohaPriceFrom: number | null = null;

  @property({ type: String, attribute: false })
  relationLabel: string = "";

  @property({ type: Number, attribute: false })
  selectedItemId: number | null = null;

  render() {
    const ellohaPriceFromFormatted =
      this.ellohaPriceFrom != null && Number.isFinite(this.ellohaPriceFrom)
        ? new Intl.NumberFormat(TranslationService.getLocale(this.language), {
            style: "currency",
            currency: "EUR",
          }).format(this.ellohaPriceFrom)
        : null;

    // Récupérer les 2 premiers champs de displayedFields
    const fieldsToShow = this.displayedFields.slice(0, 2);

    // Récupérer la typography pour le titre et le texte (fontSize optionnel : si absent, le CSS s'applique)
    const titleStyle = this.typography
      ? {
          fontFamily: StyleService.getFontFamily(this.typography.titleFont),
          color: this.typography.titleColor,
          fontSize:
            this.typography.titleSize != null
              ? `${this.typography.titleSize}px`
              : undefined,
        }
      : {
          fontFamily: "inherit",
          color: "#2F2F2F",
          fontSize: undefined as string | undefined,
        };
    const textStyle = this.typography
      ? {
          fontFamily: StyleService.getFontFamily(this.typography.textFont),
          color: this.typography.textColor,
          fontSize:
            this.typography.textSize != null
              ? `${this.typography.textSize}px`
              : undefined,
        }
      : {
          fontFamily: "inherit",
          color: "#2F2F2F",
          fontSize: undefined as string | undefined,
        };

    // Filtrer les champs non null pour gérer l'espace et le rendu
    const nonNullFields = fieldsToShow.filter(
      (field) =>
        this.getFieldData(
          this.item,
          field.id,
          this.apiFilters,
          this.language,
        ) !== null,
    );
    const fieldsCount = nonNullFields.length;
    const hasOneField = fieldsCount === 1;
    const hasTwoFields = fieldsCount === 2;
    const isSelected =
      this.selectedItemId != null && this.item.id === this.selectedItemId;

    return html`
      <div
        class="list-card ${isSelected ? "selected" : ""}"
        @click=${(e: Event) => {
          // Ne pas déclencher le clic si on clique sur le bouton de réservation
          const target = e.target as HTMLElement;
          if (target.closest(".list-card-reservation-button")) {
            e.stopPropagation();
            return;
          }
          this.onItemClick(this.item);
        }}
      >
        <div class="list-card-image-wrapper">
          <div class="card-image-fallback">
            <span class="card-image-fallback-initial"
              >${this.nom.charAt(0).toUpperCase()}</span
            >
          </div>
          ${this.imageUrl && !this.failedImages.has(this.item.id)
            ? html`<img
                class="list-card-image ${this.loadedImages.has(this.item.id)
                  ? "loaded"
                  : ""}"
                src="${this.imageUrl}"
                alt="${this.nom}"
                loading="lazy"
                @load=${() => {
                  this.onImageLoad?.(this.item.id);
                  this.requestUpdate();
                }}
                @error=${() => {
                  this.onImageError(this.item.id);
                  this.requestUpdate();
                }}
              />`
            : ""}
          ${this.hasOpenSystem
            ? html`
                <span
                  class="list-card-reservation-badge-wrap"
                  aria-label="${this.t("onlineBooking") ||
                  "Réservation en ligne"}"
                >
                  <span class="list-card-open-system-badge">
                    <apidae-icon
                      name="bold/shopping_ecommerce/cart_large_2"
                      size="18"
                      color="currentColor"
                      .visualCustomization=${this.visualCustomization}
                    ></apidae-icon>
                  </span>
                  <span
                    class="list-card-reservation-badge-bubble"
                    style="font-family: ${textStyle.fontFamily}; color: ${textStyle.color};"
                  >
                    ${this.t("onlineBooking") || "Réservation en ligne"}
                  </span>
                </span>
              `
            : ""}
          ${this.hasElloha && !this.hasOpenSystem
            ? html`
                <span
                  class="list-card-reservation-badge-wrap"
                  aria-label="${this.ellohaPriceFrom != null &&
                  Number.isFinite(this.ellohaPriceFrom)
                    ? // D'après mes tests Elloha renvoie toujours `currency=EUR` pour ces prix, indépendamment de la valeur passée dans le header `culture`.
                      `${this.t("priceFrom") || "À partir de"} ${ellohaPriceFromFormatted}`
                    : this.t("onlineBooking") || "Réservation en ligne"}"
                >
                  <span class="list-card-open-system-badge">
                    <apidae-icon
                      name="bold/shopping_ecommerce/cart_large_2"
                      size="18"
                      color="currentColor"
                      .visualCustomization=${this.visualCustomization}
                    ></apidae-icon>
                  </span>
                  <span
                    class="list-card-reservation-badge-bubble"
                    style="font-family: ${textStyle.fontFamily}; color: ${textStyle.color};"
                  >
                    ${this.ellohaPriceFrom != null &&
                    Number.isFinite(this.ellohaPriceFrom)
                      ? // D'après mes tests Elloha renvoie toujours `currency=EUR` pour ces prix, indépendamment de la valeur passée dans le header `culture`.
                        `${this.t("priceFrom") || "À partir de"} ${ellohaPriceFromFormatted}`
                      : this.t("onlineBooking") || "Réservation en ligne"}
                  </span>
                </span>
              `
            : ""}
          ${this.relationLabel
            ? html`
                <span class="list-card-relation-badge"
                  >${this.relationLabel}</span
                >
              `
            : ""}
        </div>
        <div class="list-card-text">
          <h3
            class="list-card-title"
            data-fields-count="${fieldsCount}"
            style="font-family: ${titleStyle.fontFamily}; color: ${titleStyle.color};${titleStyle.fontSize
              ? ` font-size: ${titleStyle.fontSize};`
              : ""}"
          >
            ${this.nom}
          </h3>
          <div
            class="list-card-fields"
            data-fields-count="${fieldsCount}"
            style="font-family: ${textStyle.fontFamily}; color: ${textStyle.color};${textStyle.fontSize
              ? ` font-size: ${textStyle.fontSize};`
              : ""}"
          >
            ${nonNullFields.map((field) =>
              this.renderField(field.id, textStyle, hasOneField, hasTwoFields),
            )}
          </div>
        </div>
      </div>
    `;
  }

  private renderField(
    fieldId: string,
    textStyle: any,
    hasOneField: boolean,
    hasTwoFields: boolean,
  ) {
    const fieldData = this.getFieldData(
      this.item,
      fieldId,
      this.apiFilters,
      this.language,
    );
    if (!fieldData) return null;

    // Pour reservation, c'est un bouton spécial
    if (fieldId === "reservation") {
      return html`
        <div
          class="list-card-field list-card-reservation-field"
          style="font-family: ${textStyle.fontFamily}; color: ${textStyle.color};${textStyle.fontSize
            ? ` font-size: ${textStyle.fontSize};`
            : ""}"
        >
          <div class="list-card-field-icon"></div>
          <button
            class="list-card-reservation-button"
            style="color: ${textStyle.color}; border-color: ${textStyle.color};"
            @click=${(e: Event) => {
              e.stopPropagation();
              if (fieldData.url) {
                window.open(fieldData.url, "_blank");
              }
            }}
          >
            <apidae-icon
              name="bold/messages_conversation/square_forward"
              size="18"
              color="${textStyle.color}"
              .visualCustomization=${this.visualCustomization}
            ></apidae-icon>
            <span>${this.t("reservation") || "Réservation"}</span>
          </button>
        </div>
      `;
    }

    const iconName = this.getFieldIconName(fieldId);

    return html`
      <div
        class="list-card-field"
        data-field-id="${fieldId}"
        data-has-one-field="${hasOneField}"
        data-has-two-fields="${hasTwoFields}"
        style="font-family: ${textStyle.fontFamily}; color: ${textStyle.color};${textStyle.fontSize
          ? ` font-size: ${textStyle.fontSize};`
          : ""}"
      >
        <apidae-icon
          name="${iconName}"
          size="18"
          color="${textStyle.color}"
          class="list-card-field-icon"
          .visualCustomization=${this.visualCustomization}
        ></apidae-icon>
        <span class="list-card-field-text"> ${fieldData.value} </span>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "apidae-list-item-card": ListItemCard;
  }
}
